import * as React from "react";
import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import {
  Avatar,
  Dialog,
  DialogContent,
  IconButton,
  SxProps,
  useTheme,
} from "@mui/material";
import { MouseEventHandler } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import DialogContentBody from "~/components/me/bento/content/account-small/DialogContentBody";
import { useShortAccountContent } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { MeShortAccountContentQuery, Sdk } from "~/lib/graphql/client";

const AccountSmall = ({ client }: { client: Sdk }) => {
  const result = useShortAccountContent(client);
  const theme = useTheme();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();
  const opened =
    openedContent === "AccountSmall" && result.status === "success";

  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal("AccountSmall");
  };
  const closeModal = () => {
    closeContentModal();
  };

  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status === "success" && (
        <Dialog
          open={opened}
          onClose={closeModal}
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              height: "80vh",
              m: 1,
            },
          }}
        >
          <DialogContent>
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <DialogContentBody me={result.data.me} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Body = ({
  content,
  onClick,
}: {
  content: MeShortAccountContentQuery;
  onClick: MouseEventHandler;
}) => {
  const isSpMode = useIsSpMode();
  const avatarSize = isSpMode
    ? { width: 80, height: 80 }
    : { width: 120, height: 120 };
  const iconAvatarSize: SxProps = {
    width: avatarSize.width * 0.8,
    height: avatarSize.height * 0.8,
  };

  return (
    <ContentBodyLayout onClick={onClick} label={"User Name"}>
      <div css={contentBodyStyle}>
        {content.me.avatarImageUrl ? (
          <Avatar src={content.me.avatarImageUrl} sx={avatarSize} />
        ) : (
          <Avatar sx={avatarSize}>
            <PersonIcon sx={iconAvatarSize} />
          </Avatar>
        )}
        <div css={nameStyle}>{content.me.name}</div>
      </div>
    </ContentBodyLayout>
  );
};

const contentBodyStyle = css`
  display: flex;
  margin: 0.5rem;
  gap: 0.25rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const nameStyle = css`
  width: 100%;
  flex: 1;
  display: grid;
  place-items: center;
  font-size: 20px;
  font-weight: 600;
`;

export default AccountSmall;
